/**
 * MODULE: _shared/lib/frontend/api/errors.ts
 * PURPOSE: Shared API error body type and message extraction for consistent error handling across clients
 *
 * DEPENDENCIES: Fetch API (global **`fetch`**), **`./gatewayRateLimit`**
 *
 * PUBLIC API:
 *   - ApiErrorBody: Type for backend error response bodies (FastAPI detail, or { message })
 *   - getErrorMessage(error, fallback): Extract user-facing string from ApiErrorBody or unknown
 *   - fetchWith429Retry(input, init?, opts?): **`fetch`** with shared gateway **429** cooldown (**no** multi-retry on **429** — avoids DevTools spam); skips network while **`isInGatewayBackoff`**
 *
 * SIDE EFFECTS: Network I/O when **`fetchWith429Retry`** runs **`fetch`**; **`./gatewayRateLimit`** session flag on **429**
 *
 * AGENT SAFETY: SAFE
 * FILE SIZE: 79 / 150
 * RECENT: 2026-05-08 — [2.2.874] **`fetchWith429Retry`** uses **`./gatewayRateLimit`** (no **429** retry loop; synthetic **429** when backoff active). [2.2.782] …
 */

import { bumpGateway429FromResponse, isInGatewayBackoff } from './gatewayRateLimit';

/**
 * Backend error response body shape.
 * FastAPI uses { detail: string | Array<{ msg?: string }> }; other services may use { message: string }.
 */
export interface ApiErrorBody {
  message?: string;
  detail?: string | Array<{ msg?: string }>;
}

/**
 * Extract a user-facing error message from an API error body or unknown value.
 * Use in handleResponse / fetch error paths for consistent UX.
 */
export function getErrorMessage(
  error: ApiErrorBody | unknown,
  fallback = 'Request failed'
): string {
  if (error == null) return fallback;
  const body = error as ApiErrorBody;
  if (typeof body.message === 'string' && body.message) return body.message;
  if (typeof body.detail === 'string' && body.detail) return body.detail;
  if (Array.isArray(body.detail) && body.detail[0]?.msg) return body.detail[0].msg;
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'object' && 'message' in error && typeof (error as { message: unknown }).message === 'string') {
    return (error as { message: string }).message;
  }
  return fallback;
}

/**
 * **`fetch`** for gateway-backed routes: respects app-wide **429** cooldown (**skips** **`fetch`** while
 * **`isInGatewayBackoff`** so the browser does not log another failed resource). On **429**, arms
 * **`bumpGateway429FromResponse`** and returns that response (**no** client-side retry loop — retries
 * multiplied console noise against a fixed per-minute cap).
 *
 * `opts` is retained for callers that passed **`maxAttempts`** historically; it is ignored for **429** handling.
 */
export async function fetchWith429Retry(
  input: RequestInfo | URL,
  init?: RequestInit,
  _opts?: { maxAttempts?: number; baseDelayMs?: number }
): Promise<Response> {
  void _opts;
  if (isInGatewayBackoff()) {
    return new Response(
      JSON.stringify({
        detail: 'Rate limit exceeded',
        error_code: 'RATE_LIMIT_EXCEEDED',
        message: 'Too many requests (client cooldown)',
      }),
      { status: 429, headers: { 'Content-Type': 'application/json' } },
    );
  }
  const r = await fetch(input, init);
  if (r.status === 429) {
    bumpGateway429FromResponse(r);
  }
  return r;
}
